// La fase 1 de la generación del informe sobre TanStack Query: la corrida del
// análisis (con polling mientras corre), las dudas y la hoja de hechos.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/lib/api";
import {
  cerrarCorreccionRequest,
  reabrirCorreccionRequest,
  esAnalisisEnCurso,
  fetchAnalisis,
  fetchDudas,
  escribirHechoRequest,
  fetchHoja,
  quitarHechoRequest,
  type EscrituraDeHecho,
  iniciarAnalisisRequest,
  intervaloDelAnalisis,
  responderDudaRequest,
  type RespuestaAUnaDuda,
} from "@/lib/generacion";

export const analisisKey = (informeId: number) => ["generacion", informeId, "analisis"] as const;
export const dudasKey = (informeId: number) => ["generacion", informeId, "dudas"] as const;
export const hechosKey = (informeId: number) => ["generacion", informeId, "hechos"] as const;

/**
 * La última corrida del análisis. Mientras está `en_curso` se vuelve a pedir
 * cada ~3 s; apenas deja de estarlo el intervalo se apaga solo (la función se
 * reevalúa con cada respuesta), así que una pantalla con el análisis ya
 * terminado no sondea para siempre.
 */
export function useAnalisis(informeId: number) {
  return useQuery({
    queryKey: analisisKey(informeId),
    queryFn: () => fetchAnalisis(informeId),
    enabled: Number.isFinite(informeId) && informeId > 0,
    refetchInterval: (query) => intervaloDelAnalisis(query.state.data),
  });
}

/**
 * "Analizar el expediente". Un 409 `analisis_en_curso` NO es un error: alguien
 * (otra pestaña, otra persona) ya lo lanzó, y lo que corresponde es mostrar esa
 * corrida, así que se relee en vez de avisar de una falla que no ocurrió.
 */
export function useIniciarAnalisis(informeId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      try {
        return await iniciarAnalisisRequest(informeId);
      } catch (error) {
        if (error instanceof ApiError && error.status === 409 && esAnalisisEnCurso(error.body)) {
          return null;
        }
        throw error;
      }
    },
    onSuccess: (corrida) => {
      if (corrida) queryClient.setQueryData(analisisKey(informeId), corrida);
      else void queryClient.invalidateQueries({ queryKey: analisisKey(informeId) });
    },
  });
}

export function useDudas(informeId: number, habilitada = true) {
  return useQuery({
    queryKey: dudasKey(informeId),
    queryFn: () => fetchDudas(informeId),
    enabled: habilitada && Number.isFinite(informeId) && informeId > 0,
  });
}

export function useHechos(informeId: number, habilitada = true) {
  return useQuery({
    queryKey: hechosKey(informeId),
    queryFn: () => fetchHoja(informeId),
    select: (hoja) => hoja.data,
    enabled: habilitada && Number.isFinite(informeId) && informeId > 0,
  });
}

/** Las claves que una persona puede escribir (misma consulta que la hoja). */
export function useClavesEditables(informeId: number, habilitada = true) {
  return useQuery({
    queryKey: hechosKey(informeId),
    queryFn: () => fetchHoja(informeId),
    select: (hoja) => hoja.claves_editables,
    enabled: habilitada && Number.isFinite(informeId) && informeId > 0,
  });
}

/**
 * Escribir, confirmar o quitar un hecho mueve la hoja y las secciones que lo
 * citan (quedan desactualizadas), así que se releen las dos. Las dudas también:
 * un hecho con duda abierta cambia de estado según ellas.
 */
function useRefrescarTrasUnHecho(informeId: number) {
  const queryClient = useQueryClient();

  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: hechosKey(informeId) }),
      queryClient.invalidateQueries({ queryKey: dudasKey(informeId) }),
      queryClient.invalidateQueries({ queryKey: ["generacion", informeId, "secciones"] }),
    ]);
}

export function useEscribirHecho(informeId: number) {
  const refrescar = useRefrescarTrasUnHecho(informeId);

  return useMutation({
    mutationFn: (hecho: EscrituraDeHecho) => escribirHechoRequest(informeId, hecho),
    onSuccess: refrescar,
  });
}

export function useQuitarHecho(informeId: number) {
  const refrescar = useRefrescarTrasUnHecho(informeId);

  return useMutation({
    mutationFn: (hecho: { clave: string; sujeto?: string }) => quitarHechoRequest(informeId, hecho),
    onSuccess: refrescar,
  });
}

export function useCerrarCorreccion(informeId: number) {
  const refrescar = useRefrescarDudasYHechos(informeId);

  return useMutation({
    mutationFn: ({ dudaId, motivo }: { dudaId: number; motivo: string }) =>
      cerrarCorreccionRequest(informeId, dudaId, motivo),
    onSuccess: refrescar,
  });
}

export function useReabrirCorreccion(informeId: number) {
  const refrescar = useRefrescarDudasYHechos(informeId);

  return useMutation({
    mutationFn: (dudaId: number) => reabrirCorreccionRequest(informeId, dudaId),
    onSuccess: refrescar,
  });
}

/**
 * Responder o cambiar una respuesta (el PUT sobrescribe: no hace falta reabrir
 * antes) mueve DOS cosas: la duda y los hechos que
 * esa opción fija (un hecho `con_duda` pasa a `resuelto`), así que se releen
 * las dos listas.
 */
function useRefrescarDudasYHechos(informeId: number) {
  const queryClient = useQueryClient();

  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: dudasKey(informeId) }),
      queryClient.invalidateQueries({ queryKey: hechosKey(informeId) }),
      // La puerta de la redacción (dudas sin responder, correcciones pendientes) viaja con las secciones.
      queryClient.invalidateQueries({ queryKey: ["generacion", informeId, "secciones"] }),
    ]);
}

export function useResponderDuda(informeId: number) {
  const refrescar = useRefrescarDudasYHechos(informeId);

  return useMutation({
    mutationFn: ({ dudaId, respuesta }: { dudaId: number; respuesta: RespuestaAUnaDuda }) =>
      responderDudaRequest(informeId, dudaId, respuesta),
    onSuccess: refrescar,
    // Una duda que dejó de ser vigente responde 409: la lista que se ve está vieja.
    onError: refrescar,
  });
}
